<template>
  <div>
    <!-- Central Dispatch Negotiations -->
    <div class="card mb-4" v-if="negotiations.length > 0">
      <h5 class="card-header">Central Dispatch Negotiations</h5>
      <div class="card-body">
        <LoadingIndicator v-if="negotiationsLoading" centered />
        <div v-else>
          <div v-for="negotiation in negotiations" :key="negotiation.negotiationId" class="negotiation-item mb-4 pb-3 border-bottom">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h6 class="mb-1">
                  <b-link v-if="negotiation.carrier" :to="{ name: 'CarrierDetails', params: { id: negotiation.carrier.id } }">
                    {{ negotiation.carrier.company }}
                  </b-link>
                  <b-badge :variant="negotiationStatusBadge(negotiation).variant" class="ml-2">
                    {{ negotiationStatusBadge(negotiation).text }}
                  </b-badge>
                </h6>
                <small class="text-muted" v-if="negotiation.carrier.phone">
                  {{ negotiation.carrier.phone }}
                </small>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="card bg-light mb-2">
                  <div class="card-body p-2">
                    <div class="font-weight-bold text-primary">Their Latest Offer</div>
                    <div class="mt-1">
                      <strong>Amount:</strong> ${{ negotiation.latestCarrierOffer.carrierPay }}
                    </div>
                    <div><strong>Pickup:</strong> {{ negotiation.latestCarrierOffer.pickupDate | formatDate }}</div>
                    <div><strong>Delivery:</strong> {{ negotiation.latestCarrierOffer.deliveryDate | formatDate }}</div>
                    <div v-if="negotiation.latestCarrierOffer.expiresAt">
                      <small :class="isOfferExpired(negotiation.latestCarrierOffer) ? 'text-danger font-weight-bold' : 'text-muted'">
                        {{ isOfferExpired(negotiation.latestCarrierOffer) ? 'Expired' : 'Expires' }}:
                        {{ negotiation.latestCarrierOffer.expiresAt | formatDateTime }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6" v-if="negotiation.latestCounterOffer">
                <div class="card bg-light mb-2">
                  <div class="card-body p-2">
                    <div class="font-weight-bold text-success">Our Latest Counter</div>
                    <div class="mt-1">
                      <strong>Amount:</strong> ${{ negotiation.latestCounterOffer.carrierPay }}
                    </div>
                    <div><strong>Pickup:</strong> {{ negotiation.latestCounterOffer.pickupDate | formatDate }}</div>
                    <div><strong>Delivery:</strong> {{ negotiation.latestCounterOffer.deliveryDate | formatDate }}</div>
                    <div v-if="negotiation.latestCounterOffer.user">
                      <small class="text-muted">By: {{ negotiation.latestCounterOffer.user.name }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Counter Offer History -->
            <div v-if="negotiation.counterOfferHistory && negotiation.counterOfferHistory.length > 1" class="mt-2">
              <b-button v-b-toggle="`history-${negotiation.negotiationId}`" variant="link" size="sm" class="p-0">
                View Counter Offer History ({{ negotiation.counterOfferHistory.length }})
              </b-button>
              <b-collapse :id="`history-${negotiation.negotiationId}`" class="mt-2">
                <div class="history-list">
                  <div v-for="counter in negotiation.counterOfferHistory" :key="counter.id" class="history-item p-2 bg-light mb-1">
                    <small>
                      <strong>${{ counter.carrierPay }}</strong> -
                      {{ counter.pickupDate | formatDate }} to {{ counter.deliveryDate | formatDate }}
                      <span v-if="counter.user" class="text-muted ml-2">by {{ counter.user.name }}</span>
                      <span class="text-muted ml-2">{{ counter.createdAt | formatDateTime }}</span>
                    </small>
                  </div>
                </div>
              </b-collapse>
            </div>

            <!-- Action Buttons -->
            <div class="mt-3" v-if="negotiation.isActive && !order.carrier">
              <b-btn
                size="sm"
                variant="primary"
                @click="handleCounterOffer(negotiation)"
                :disabled="isOfferExpired(negotiation.latestCarrierOffer)"
                class="mr-2"
              >
                Send Counter Offer
              </b-btn>
              <b-btn
                size="sm"
                variant="success"
                @click="handleAcceptOffer(negotiation)"
                :disabled="isOfferExpired(negotiation.latestCarrierOffer)"
                class="mr-2"
              >
                Accept Offer
              </b-btn>
              <b-btn
                size="sm"
                variant="danger"
                @click="handleDeclineOffer(negotiation)"
                :disabled="isOfferExpired(negotiation.latestCarrierOffer)"
              >
                Decline
              </b-btn>
            </div>
            <div class="mt-2 text-muted" v-else-if="order.carrier">
              <small>Order already dispatched</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Manual Added Truckers -->
    <template>
      <div class="card mb-4">
        <h5 class="card-header">Added Truckers</h5>
        <div class="card-body">
          <LoadingIndicator v-if="offersLoading" centered />
          <div v-else>
            <table class="table" v-if="offers.length">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Carrier</th>
                  <th>Pickup Date</th>
                  <th>Delivery Date</th>
                  <th>Transport type</th>
                  <th>Carrier pay</th>
                  <th>Note</th>
                  <th>Created at</th>
                  <th width="15%"></th>
                  <!-- <th></th> -->
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in offers" :key="item.id" :class="rowClass(item)">
                  <td>{{ item.user && item.user.name }}</td>
                  <td>
                    <b-link v-if="item.carrier" :to="carrierLink(item.carrier)">{{ item.carrier.contact.company }}
                    </b-link>
                  </td>
                  <td>{{ dateTypeLabel(item.pickupDateTypeId) }} {{ item.pickupDate | formatDate }}</td>
                  <td>{{ dateTypeLabel(item.deliveryDateTypeId) }} {{ item.deliveryDate | formatDate }}</td>
                  <td>{{ transportTypeName(item.transportTypeId) }}</td>
                  <td>{{ item.carrierPay }}</td>
                  <td>{{ item.note }}</td>
                  <td>{{ item.createdAt | formatDateTime }}</td>
                  <td>
                    <b-btn size="xs" variant="primary" v-if="!order.carrier" @click="handleDispatching(order, item)">
                      Dispatch</b-btn>
                  </td>
                  <!-- <td><b-link class="btn btn-primary btn-xs" v-if="item.carrier" :to="dispatchLink(item.carrier)">Dispatch</b-link></td> -->
                </tr>
              </tbody>
              <DispatchModal v-if="dispatching" :order="dispatchingOrder" :offer="dispatchingOffer"
                @refresh="$emit('refresh')" :on-close="() => (dispatching = null)"></DispatchModal>
            </table>
            <div class="alert alert-warning" v-else>No truckers have been added</div>
          </div>
        </div>

        <div class="card-body border border-top">
          <b-form @submit.prevent="submit" inline>
            <b-form-group label="Carrier" class="mr-3 carrier">
              <CarrierPicker @input="setCarrier" />
            </b-form-group>
            <b-form-group label="Pickup Date" class="mr-3 cdate">
              <flat-pickr class="" v-model="form.pickupDate" placeholder="Select date" :config="fltConfig" value="" />
            </b-form-group>
            <b-form-group label="Pickup date type" class="mr-3">
              <b-select class="custom-select" v-model="form.pickupDateTypeId" :options="dateTypeOptions" />
            </b-form-group>
            <b-form-group label="Delivery Date" class="mr-3 cdate">
              <flat-pickr class="" v-model="form.deliveryDate" placeholder="Select date" ref="datePicker"
                :config="fltConfig2" value="" />
            </b-form-group>
            <b-form-group label="Delivery date type" class="mr-3">
              <b-select class="custom-select" v-model="form.deliveryDateTypeId" :options="dateTypeOptions" />
            </b-form-group>
            <b-form-group label="Transport type" class="mr-3">
              <b-select class="custom-select" v-model="form.transportTypeId" :options="transportTypeOptions" />
            </b-form-group>
            <b-form-group label="Carrier Pay" class="mr-3">
              <b-input class="cpay" v-model="form.carrierPay" type="text" :number="true" />
            </b-form-group>
            <b-form-group label="Note" class="mr-3">
              <b-input v-model="form.note" type="text" />
            </b-form-group>
            <button class="btn btn-primary mt-4" :disabled="formSubmit ? 'disabled' : undefined">Create</button>
          </b-form>
        </div>
      </div>
    </template>

    <!-- Modals -->
    <CounterOfferModal v-if="counterOffering" :order="order" :negotiation="counterOfferNegotiation"
      @refresh="() => { $emit('refresh'); fetchNegotiations(); }" :on-close="() => (counterOffering = false)"></CounterOfferModal>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import CarrierPicker from './CarrierPicker';
import LoadingIndicator from './LoadingIndicator.vue';
import DispatchModal from './DispatchModal.vue';
import CounterOfferModal from './CounterOfferModal.vue';
import dateTypesMixin from '@/mixins/dateTypes';
import transportTypesMixin from '@/mixins/transportTypes';

export default {
  name: 'OrdersOffers',
  data() {
    let self = this;
    return {
      offers: [],
      negotiations: [],
      carriers: [],
      searchTerm: '',
      offersLoading: true,
      negotiationsLoading: false,
      formSubmit: false,
      dispatching: false,
      dispatchingOrder: null,
      dispatchingOffer: null,
      counterOffering: false,
      counterOfferNegotiation: null,
      fltConfig: {
        inline: false,
        enableTime: false,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'm/d/Y',
        allowInput: false,
        onChange: function () {
          if (!self.form.deliveryDate) {
            self.$refs.datePicker.fp.open();
          }
        },
      },
      fltConfig2: {
        inline: false,
        enableTime: false,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'm/d/Y',
        allowInput: false,
        minDate: this.order.moveDate,
      },
      form: {
        id: null,
        leadId: this.order.id,
        pickupDate: this.order.moveDate,
        pickupDateTypeId: this.order.pickupDateTypeId,
        deliveryDate: null,
        deliveryDateTypeId: this.order.deliveryDateTypeId,
        transportTypeId: this.order.transportTypeId,
        carrierPay: this.order.carrierPay ? parseInt(this.order.carrierPay) : null,
        carrierId: null,
        note: null,
      },
      transportTypeOptions: [
        { value: null, text: '-' },
     //   { value: 1, text: 'Open' },
     //   { value: 2, text: 'Enclosed' },
      ],
      dateTypeOptions: [
        { value: null, text: '-' },
      ],
    };
  },
  mixins: [dateTypesMixin, transportTypesMixin],
  components: {
    CarrierPicker,
    LoadingIndicator,
    DispatchModal,
    CounterOfferModal,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  computed: {
  },
  created() {
    this.transportTypeOptions = this.transportTypeOptions.concat(this.transportTypes.map(x => {x.text = x.label; return x}));
    this.dateTypeOptions = this.dateTypeOptions.concat(this.dateTypes.map(x => {x.text = x.label; return x}));
    this.fetch();
    this.fetchNegotiations();
  },
  methods: {
    handleDispatching(order, offer) {
      this.dispatching = true;
      this.dispatchingOrder = order;
      this.dispatchingOffer = offer;
    },
    async dispatchOrder(carrierId) {
      const response = await ApiClient.leads.dispatch.post({
        leadId: this.order.id,
        body: { carrierId: carrierId },
      });
      if (response.status === 200) {
        this.errors = {};
        this.$emit('refresh');
      } else if (response.status === 400) {
        this.errors = { generalError: response.data.message };
      } else if (response.status === 422) {
        this.errors = response.data.errors;
      }
    },
    transportTypeName(id) {
      if (id == 1) return 'Open';
      if (id == 2) return 'Enclosed';
      return '-';
    },
    setCarrier(carrier) {
      this.form.carrierId = carrier ? carrier.id : null;
    },
    async submit() {
      this.formSubmit = true;
      let form = Object.assign({}, this.form);
      const response = await ApiClient.leads.offers.post({ leadId: this.order.id, body: form });
      if (response.status === 200) {
        this.reset();
        this.fetch();
      }
      this.formSubmit = false;
    },
    reset() {
      this.form = Object.assign(this.form, {
        id: null,
        leadId: this.order.id,
        pickupDate: this.order.moveDate,
        pickupDateTypeId: this.order.pickupDateTypeId,
        deliveryDate: null,
        deliveryDateTypeId: this.order.deliveryDateTypeId,
        transportTypeId: this.order.transportTypeId,
        carrierPay: this.order.carrierPay,
        carrierId: null,
        note: null,
      });
    },
    async fetch() {
      this.offersLoading = true;
      const offersResponse = await ApiClient.leads.offers.get({ leadId: this.order.id });
      this.offers = offersResponse.data.data;
      this.offersLoading = false;
    },
    async fetchNegotiations() {
      this.negotiationsLoading = true;
      try {
        const response = await ApiClient.leads.negotiations.get({ leadId: this.order.id });
        if (response.status === 200) {
          this.negotiations = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching negotiations:', error);
      }
      this.negotiationsLoading = false;
    },
    handleCounterOffer(negotiation) {
      if (this.isOfferExpired(negotiation.latestCarrierOffer)) {
        this.$bvToast.toast('This offer has expired and cannot be countered.', {
          title: 'Offer Expired',
          variant: 'warning',
          solid: true,
        });
        return;
      }
      this.counterOffering = true;
      this.counterOfferNegotiation = negotiation;
    },
    async handleAcceptOffer(negotiation) {
      if (this.isOfferExpired(negotiation.latestCarrierOffer)) {
        this.$bvToast.toast('This offer has expired and cannot be accepted.', {
          title: 'Offer Expired',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to accept the offer of $${negotiation.latestCarrierOffer.carrierPay} from ${negotiation.carrier.company}?`,
        {
          title: 'Accept Offer',
          okVariant: 'success',
          okTitle: 'Accept',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );

      if (!confirmed) return;

      const response = await ApiClient.leads.offers.accept({
        leadId: this.order.id,
        id: negotiation.latestCarrierOffer.id,
      });

      if (response.status === 200) {
        this.$bvToast.toast('Offer accepted successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.$emit('refresh');
        this.fetchNegotiations();
      } else if (response.status === 400) {
        this.$bvToast.toast(response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } else {
        this.$bvToast.toast('An error occurred. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    },
    async handleDeclineOffer(negotiation) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to decline the offer from ${negotiation.carrier.company}? This action cannot be undone.`,
        {
          title: 'Decline Offer',
          okVariant: 'danger',
          okTitle: 'Decline',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );

      if (!confirmed) return;

      const response = await ApiClient.leads.offers.decline({
        leadId: this.order.id,
        id: negotiation.latestCarrierOffer.id,
      });

      if (response.status === 200) {
        this.$bvToast.toast('Offer declined successfully.', {
          title: 'Success',
          variant: 'info',
          solid: true,
        });
        this.fetchNegotiations();
      } else if (response.status === 400) {
        this.$bvToast.toast(response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } else {
        this.$bvToast.toast('An error occurred. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    },
    isOfferExpired(offer) {
      if (!offer.expiresAt) return false;
      return new Date(offer.expiresAt) < new Date();
    },
    negotiationStatusClass(negotiation) {
      if (!negotiation.isActive) return 'text-muted';
      if (this.isOfferExpired(negotiation.latestCarrierOffer)) return 'text-danger';
      if (negotiation.status === 'accepted') return 'text-success';
      if (negotiation.status === 'declined') return 'text-danger';
      return '';
    },
    negotiationStatusBadge(negotiation) {
      if (this.isOfferExpired(negotiation.latestCarrierOffer)) return { text: 'Expired', variant: 'danger' };
      if (negotiation.status === 'accepted') return { text: 'Accepted', variant: 'success' };
      if (negotiation.status === 'declined') return { text: 'Declined', variant: 'danger' };
      if (negotiation.isActive) return { text: 'Active', variant: 'warning' };
      return { text: 'Inactive', variant: 'secondary' };
    },
    rowClass(item) {
      if (item.deletedAt) return 'text-secondary';
      return '';
    },
    async remove(id) {
      await ApiClient.leads.offers.delete({ leadId: this.order.id, id: id });
      this.fetch();
    },
    carrierLink(carrier) {
      return carrier ? { name: 'CarrierDetails', params: { id: carrier.id } } : null;
    },
    dispatchLink(carrier) {
      return carrier ? { name: 'OrderDispatch', params: { id: this.order.id }, query: { carrierId: carrier.id } } : null;
    },
  },
  watch: {
    'form.pickupDate': function (val) {
      this.fltConfig2.minDate = val;
    },
  }
};
</script>

<style lang="scss" >
.carrier {
  width: 300px;
}

.cpay {
  width: 90px;
}

.cdate .form-control {
  width: 110px;
}

.negotiation-item {
  &:last-child {
    border-bottom: none !important;
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
}

.history-list {
  max-height: 300px;
  overflow-y: auto;
  border-left: 3px solid #e9ecef;
  padding-left: 10px;
}

.history-item {
  border-radius: 4px;
  font-size: 0.875rem;
}
</style>
